import { isFeatureFlagEnabled } from '@repo/feature-flag';
import { TestId } from '@repo/test-id';
import { lazyWithRetry } from '@repo/error';
import { DESKTOP_MEDIA_QUERY, useMediaQuery } from '@repo/ui';
import { Suspense, useEffect, useState, type CSSProperties } from 'react';
import { generatePath, useMatch, useNavigate } from 'react-router-dom';

import {
  DiscountModal,
  DurationBar,
  FiltersModal,
  LocationPermissionDialog,
  NewsToast,
  Provider,
} from '@features/parkingMap';
import TimeContractPickerModal from '@features/parkingSearch/components/duration/TimeContractPickerModal';
import InstantInputDrawer from '@features/parkingSearch/components/parkStart/parkStartDrawer/InstantInputDrawer';
import MapLayoutDialog from '@features/parkingSearch/components/MapLayoutDialog';
import DesktopBar from '@features/parkingSearch/components/DesktopBar';

import BottomBar from '@shared/components/bottomBar/BottomBar';
import { useParkingSearchStore } from '@shared/stores/useParkingSearchStore';
import DesktopFooter from '@shared/components/DesktopFooter';

import { ROUTE_PATHS } from '@router/paths';
import { RouteName } from '@router/routeNames';

const ParkingQrCodeOverlay = lazyWithRetry(() =>
  import('@features/parkingQrCode').then((module) => ({ default: module.ParkingQrCodeOverlay }))
);

export default function MapLayout() {
  const navigate = useNavigate();
  const setSelectedParking = useParkingSearchStore((state) => state.setSelectedParking);
  const setSelectedSpot = useParkingSearchStore((state) => state.setSelectedSpot);
  const testId = TestId.root('map');
  const isDiscountBannerEnabled = isFeatureFlagEnabled('parkingDiscountBanner');

  const isMapRoute = Boolean(useMatch(ROUTE_PATHS[RouteName.MAP]));
  const isParkingSearchResultsRoute = Boolean(useMatch(ROUTE_PATHS[RouteName.PARKING_SEARCH_RESULTS]));
  const isParkingDetailsRoute = Boolean(useMatch(ROUTE_PATHS[RouteName.PARKING_DETAILS]));
  const isMdScreen = useMediaQuery(DESKTOP_MEDIA_QUERY);
  const isDurationBarVisible = isParkingSearchResultsRoute || isParkingDetailsRoute;
  // DurationBar is mobile-only, so on desktop the news always has its corner.
  const isNewsToastVisible = isMdScreen || !isDurationBarVisible;

  useEffect(() => {
    if (isMapRoute && isMdScreen) {
      navigate(ROUTE_PATHS[RouteName.PARKING_SEARCH_RESULTS], { replace: true });
    }
  }, [isMapRoute, isMdScreen, navigate]);

  const isTimeContractPickerModalOpen = useParkingSearchStore((state) => state.isTimeContractPickerModalOpen);
  const setIsTimeContractPickerModalOpen = useParkingSearchStore((state) => state.setIsTimeContractPickerModalOpen);

  const [isQrScannerVisible, setIsQrScannerVisible] = useState<boolean>(false);
  const [isDiscountDrawerOpen, setIsDiscountDrawerOpen] = useState<boolean>(false);
  const handleToggleQr = () => {
    setIsQrScannerVisible((prev) => !prev);
  };

  const handleCloseQrScanner = () => {
    setIsQrScannerVisible(false);
  };

  const handleQrCodeScanned = (parkingId: string, parkingSlug: string, spotId?: string, spotName?: string): void => {
    setSelectedParking(parkingId, parkingSlug);
    setSelectedSpot(spotId, spotName);
    navigate(generatePath(ROUTE_PATHS[RouteName.RESERVATION_QR]));
  };

  const [drawerHeight, setDrawerHeight] = useState<number>(0);

  const handleOpenDiscountDrawer = () => setIsDiscountDrawerOpen(true);

  return (
    <div className="relative flex h-full w-full flex-col overflow-hidden">
      <DesktopBar testId={testId.append('bar')} />

      <div className="relative flex-1 overflow-hidden">
        <div
          className="relative h-full w-full overflow-hidden pb-(--drawer-height) md:pb-0"
          style={{ '--drawer-height': `${drawerHeight}px` } as CSSProperties}
        >
          {isQrScannerVisible && (
            <Suspense fallback={null}>
              <ParkingQrCodeOverlay
                testId={testId.append('qrScanner')}
                onClose={handleCloseQrScanner}
                onCodeDelivered={handleQrCodeScanned}
              />
            </Suspense>
          )}

          <Provider testId={testId.append('map')} />

          {isDurationBarVisible && <DurationBar testId={testId.append('selectedTimeDisplay')} />}

          {isNewsToastVisible && <NewsToast testId={testId.append('news')} />}

          <MapLayoutDialog
            testId={testId}
            onDrawerHeightChange={setDrawerHeight}
            onOpenDiscountDrawer={handleOpenDiscountDrawer}
            onToggleQr={handleToggleQr}
          />
          <LocationPermissionDialog testId={testId.append('locationPermission')} />
          <FiltersModal testId={testId.append('parkingSearchFilters')} />
          {isDiscountBannerEnabled && (
            <DiscountModal
              open={isDiscountDrawerOpen}
              onOpenChange={setIsDiscountDrawerOpen}
              testId={testId.append('discountDrawer')}
            />
          )}
          <TimeContractPickerModal
            open={isTimeContractPickerModalOpen}
            onOpenChange={setIsTimeContractPickerModalOpen}
            testId={testId.append('durationDialog')}
          />
          <InstantInputDrawer testId={testId.append('parkStartDrawer')} />
        </div>
      </div>
      <div className="md:hidden">
        <BottomBar />
      </div>
      <DesktopFooter />
    </div>
  );
}
